import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Injector, input, OnInit } from '@angular/core';
import { CreateEventFormStore } from '../../new-event-form/_services/create-event-form-store.service';
import { AdditionalDetailsForm } from '../../_models/event-form.model';
import { FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/internal/operators/tap';
import { MatDialog } from '@angular/material/dialog';
import { BlurWarningDialogComponent } from './blur-warning-dialog/blur-warning-dialog.component';
import { firstValueFrom } from 'rxjs';
import { EditEventFormStore } from '../../edit-event-form/_services/edit-event-form-store.service';

@Component({
  selector: 'additonal-details-step',
  standalone: false,
  templateUrl: './additonal-details-step.component.html',
  styleUrl: './additonal-details-step.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdditonalDetailsStepComponent implements OnInit {
  edit = input<boolean>(false);

  public destroyRef = inject(DestroyRef);

  private injector = inject(Injector);

  public eventFormStore = computed(() =>
    this.edit()
      ? this.injector.get(EditEventFormStore)
      : this.injector.get(CreateEventFormStore)
  );

  public formGroup = input.required<FormGroup<AdditionalDetailsForm>>();

  public highImpactEvent = computed(() => this.eventFormStore().isHighImpactEvent());

  private hasInit: boolean = false;

  private readonly dialog = inject(MatDialog);

  constructor() {
    effect(() => {
      const formGroup = this.formGroup();
      const automaticBlur = this.eventFormStore().automaticBlur();
      const highImpact = this.highImpactEvent();

      if(!this.hasInit) {
        this.hasInit = true;
        return;
      }

      if(automaticBlur) {
        formGroup.controls.privacyEnabled.setValue(highImpact);
      } else  {
        formGroup.controls.helpFunctionForm.reset();
      }
    });
  }

  public ngOnInit(): void {
    const highImpactControl = this.formGroup().controls.highImpactEvent;
    const emergencyControl = this.formGroup().controls.emergencyServicesPresent;

    if (highImpactControl.value === null || highImpactControl.value === undefined) {
      highImpactControl.setValue(false);
    }

    highImpactControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((value) => {
        applyEmergencyValidator(value === true);
        this.formGroup().updateValueAndValidity({ emitEvent: false });
      })
    ).subscribe();

    this.formGroup().valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(value => {
        this.eventFormStore().setAdditionalDetailsFormValue(value);
      })
    ).subscribe();

    this.formGroup().controls.helpFunctionForm.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(value => {
        if(value) {
          const hasAnyChecked = Object.values(value).some(c => c === true);
          this.formGroup().controls.privacyEnabled.setValue(hasAnyChecked);
        }
      })
    ).subscribe();

    this.formGroup().statusChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(value => {
        this.eventFormStore().stepPrivacyValid.set(value === 'VALID');
      })
    ).subscribe();

    this.eventFormStore().registerPrivacyStepLeaveGuard(() => this.checkChecklistBlur());
    this.destroyRef.onDestroy(() => {
      this.eventFormStore().registerPrivacyStepLeaveGuard(null);
    });

    if(this.eventFormStore().automaticBlur()) {
      this.formGroup().controls.privacyEnabled.setValue(this.highImpactEvent());
    }

    const applyEmergencyValidator = (isHighImpact: boolean) => {
      if (isHighImpact) {
        emergencyControl.setValidators(Validators.required);
      } else {
        emergencyControl.clearValidators();
        emergencyControl.reset();
      }
      emergencyControl.updateValueAndValidity({ emitEvent: false });
    };

    applyEmergencyValidator(highImpactControl.value === true);

    if(this.formGroup().valid) {
      this.eventFormStore().stepPrivacyValid.set(true);
    }
  }

  public async checkChecklistBlur(): Promise<boolean> {
    if(this.eventFormStore().automaticBlur()) {
      return true;
    }

    // User has privacy off on a highImpact event
    if(!this.formGroup().controls.privacyEnabled.value && 
      this.highImpactEvent()
    ) {
      const result = await firstValueFrom(
        this.dialog.open<BlurWarningDialogComponent>(BlurWarningDialogComponent, {
          data: undefined,
        }).afterClosed()
      );

      const proceed = result === 'proceed' || result?.result === 'proceed';
      return proceed;
    }

    return true;
  }
}
